import { type FormikProps, type FormikValues } from "formik"
import { type OnboardingFormValues } from "@/features/onboarding/wizard"
import { type OnboardingFormValues as OnboardingFormValuesLegacy } from "@/features/onboardingLegacy/wizard"
import { type Partner } from "../constants/partner"
import { type PartnerSessionType } from "../types/partnerSession"
import { getPartnerPlugin } from "./registry"
import { type PartnerPluginSlot } from "./types"

type PartnerPluginProps<TPartner extends Partner = Partner> = {
    slot: PartnerPluginSlot
    partnerSlug: TPartner | null
    formValues?: OnboardingFormValues | OnboardingFormValuesLegacy
    values?: FormikProps<FormikValues>["values"]
    session?: PartnerSessionType<TPartner> | null
}

/**
 * To add a new plugin:
 * 1. Add a new slot to PartnerPluginSlot in types.ts
 * 2. Create your component in the partner's folder (e.g., plugins/darty/MyComponent.tsx)
 * 3. Register it in the partner's index.ts file
 */
export function PartnerPlugin<TPartner extends Partner>({
    slot,
    partnerSlug,
    formValues,
    values,
    session,
}: PartnerPluginProps<TPartner>) {
    const optionalProps = {
        ...(values ? { values } : {}),
        ...(formValues ? { formValues } : {}),
        ...(session ? { session } : {}),
    }

    const PluginComponent = getPartnerPlugin(partnerSlug, slot)

    if (!PluginComponent) {
        return null
    }

    return <PluginComponent {...optionalProps} />
}

PartnerPlugin.messages = [...getPartnerPlugin.messages] as const
